import type { QueryClient } from '@tanstack/react-query';
import { berlinIsoDate } from '../../lib/components/compound/pxtraCardWallet/berlinCalendar';

/** Everything the wallet page caches for a company; invalidating it refetches all of it. */
export const walletKey = (companyId: string) => ['wallet', companyId];

/**
 * The Berlin day goes into the keys of the answers that depend on it (escalation
 * countdown, current month), so a tab left open overnight refetches them.
 */
export const walletDay = (today: Date) => berlinIsoDate(today.toISOString());

export const walletSummaryKey = (companyId: string, day: string) => [
  ...walletKey(companyId),
  'summary',
  day
];

export const walletUpcomingKey = (companyId: string, from: string, until: string, day: string) => [
  ...walletKey(companyId),
  'upcoming',
  from,
  until,
  day
];

export const walletTransactionsKey = (companyId: string, year: number) => [
  ...walletKey(companyId),
  'transactions',
  year
];

/** `placeholderData` that carries the previous key's answer over, but never another company's. */
export const keepForCompany =
  (companyId: string) =>
  <T>(previousData: T | undefined, previousQuery: { queryKey: readonly unknown[] } | undefined) =>
    previousQuery?.queryKey[1] === companyId ? previousData : undefined;

/**
 * Query errors drop placeholder data, so after a failed refetch the page would lose an
 * answer from earlier today or yesterday; this finds the newest summary still cached.
 */
export const latestCachedSummary = <T>(queryClient: QueryClient, companyId: string) => {
  const queries = queryClient
    .getQueryCache()
    .findAll({ queryKey: [...walletKey(companyId), 'summary'] })
    .filter((query) => query.state.data !== undefined);
  const newest = queries.sort((a, b) => b.state.dataUpdatedAt - a.state.dataUpdatedAt)[0];
  return newest && { data: newest.state.data as T, dataUpdatedAt: newest.state.dataUpdatedAt };
};
